import { useState } from 'react'
import { FormSheet, OfficialUseBlock, SectionBand } from './FormSheet'
import { signIn, type Staff } from '../lib/session'

/**
 * The door. Form DC-99 is departmental paperwork, so it asks who you are before
 * it shows you anything -- the unlisted path is no longer what keeps the public
 * out of it.
 */
export function StaffSignIn({ onSignedIn }: { onSignedIn: (staff: Staff) => void }) {
  const [emailAddress, setEmailAddress] = useState('')
  const [password, setPassword] = useState('')
  const [refusal, setRefusal] = useState('')
  const [checking, setChecking] = useState(false)

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (checking) return

    if (!emailAddress.trim() || !password) {
      setRefusal('Both fields are required.')
      return
    }

    setRefusal('')
    setChecking(true)
    try {
      onSignedIn(await signIn(emailAddress.trim(), password))
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : 'Those credentials were not recognised.')
      setChecking(false)
    }
  }

  return (
    <FormSheet form="Form DC-99 (Internal)" control="Departmental use only">
      <SectionBand title="Identification required" />

      <form onSubmit={onSubmit} noValidate>
        <div className="px-4 pb-6 pt-5 sm:px-7">
          <p className="font-form text-[11px] leading-relaxed text-carbon/70">
            This page is for departmental staff. A complainant has no business here and needs no
            account; return to Form DC-1 to file.
          </p>

          <div className="mt-5">
            <label
              htmlFor="staff-email"
              className="font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
            >
              Email address
            </label>
            <input
              id="staff-email"
              type="email"
              value={emailAddress}
              autoComplete="username"
              onChange={(e) => setEmailAddress(e.target.value)}
              className="mt-2 w-full border-b border-carbon/50 bg-transparent px-1 pb-1 font-type text-[17px] text-carbon outline-none focus:border-process"
            />
          </div>

          <div className="mt-5">
            <label
              htmlFor="staff-password"
              className="font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
            >
              Password
            </label>
            <input
              id="staff-password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border-b border-carbon/50 bg-transparent px-1 pb-1 font-type text-[17px] text-carbon outline-none focus:border-process"
            />
          </div>

          {refusal && (
            <p
              role="alert"
              className="mt-5 border-l-4 border-stamp bg-stamp/5 px-3 py-2 font-form text-sm font-semibold text-stamp"
            >
              {refusal}
            </p>
          )}

          <button
            type="submit"
            disabled={checking}
            className="mt-6 w-full bg-carbon px-6 py-3.5 font-form text-sm font-bold uppercase tracking-[0.22em] text-file transition-colors hover:bg-process disabled:cursor-not-allowed disabled:bg-carbon/40"
          >
            {checking ? 'Verifying…' : 'Sign in'}
          </button>
        </div>

        {/* The department does not take applications. */}
        <OfficialUseBlock />
      </form>
    </FormSheet>
  )
}
